import { FC, HTMLAttributes, InputHTMLAttributes } from 'react';
import { ImageProps } from 'react-bootstrap';
import { fileMetaOf } from 'web-utility';

import { ImagePreview } from './ImagePreview';

export type FilePreviewProps = ImageProps &
  HTMLAttributes<HTMLAudioElement> &
  HTMLAttributes<HTMLVideoElement> &
  HTMLAttributes<HTMLAnchorElement> & {
    type?: InputHTMLAttributes<HTMLInputElement>['accept'];
    path?: string;
    file?: HTMLMediaElement['srcObject'];
  };

export const FilePreview: FC<FilePreviewProps> = ({
  className = '',
  style,
  hidden,
  type,
  path,
  file,
  ...props
}) => {
  if (!file && !path) throw new URIError('No file or path provided');

  const { name, category, extension } =
    file instanceof EventTarget
      ? { category: type?.split('/')[0] }
      : fileMetaOf(file || path || '');

  return (
    <figure
      className={`d-flex flex-column align-items-center justify-content-center m-0 ${className}`}
      {...{ style, hidden }}
    >
      {category === 'image' ? (
        <ImagePreview
          className="h-100"
          fluid
          loading="lazy"
          src={file instanceof Blob ? file : path}
          {...props}
        />
      ) : category === 'audio' ? (
        <audio
          controls
          src={path}
          ref={node => {
            if (node) node.srcObject = file || null;
          }}
          {...props}
        />
      ) : category === 'video' ? (
        <video
          muted
          src={path}
          ref={node => {
            if (node) node.srcObject = file || null;
          }}
          onMouseEnter={({ currentTarget }) => currentTarget.play()}
          onMouseLeave={({ currentTarget }) => currentTarget.pause()}
          {...props}
        />
      ) : (
        <>
          <a
            className="d-inline-flex justify-content-center align-items-center"
            href={path}
            target="_blank"
            download={name}
            {...props}
          >
            <i className={`bi bi-filetype-${extension || 'file-earmark'} fs-1`} />
          </a>
          <figcaption className="mw-100 text-wrap text-truncate">{name}</figcaption>
        </>
      )}
    </figure>
  );
};

FilePreview.displayName = 'FilePreview';
